import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { IDSInput, IDSSelect, IDSButton, IDSButtonGroup, IDSCheckButton, IDSSpinner, IDSAlert } from '@inera/ids-react';
import { fetchOrganisations } from "../../services/adminAPI";
import { fetchContracts } from "../../services/contractsAPI";
import { fetchTestersByOrganisation } from "../../services/usersAPI";
import { createServiceOrder, fetchContractsByTester } from "../../services/serviceOrderAPI";

const CreateUserLayout = () => {
  const { isAuthenticated } = useAuth();
  const [mode, setMode] = useState('SjD');
  const [formData, setFormData] = useState({
    organisationId: '',
    serviceNowId: '',
    tpvIssueKey: ''
  });
  const [selectedContractIds, setSelectedContractIds] = useState(new Set());
  const [selectedTesterEmails, setSelectedTesterEmails] = useState(new Set());
  const [testerContractMap, setTesterContractMap] = useState({});
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [testers, setTesters] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchOrganisations()
      .then(setOrganisations)
      .catch(err => console.error('Failed to fetch organisations:', err));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchContracts()
      .then(setContracts)
      .catch(err => console.error('Failed to fetch contracts:', err));
  }, [isAuthenticated]);

  const ineraOrg = organisations.find(o => o.name === 'Inera');

  useEffect(() => {
    if (!isAuthenticated) return;
    const testerOrgId = mode === 'VavTP' ? ineraOrg?.id : formData.organisationId;
    if (!testerOrgId) { setTesters([]); return; }
    fetchTestersByOrganisation(testerOrgId)
      .then(setTesters)
      .catch(err => console.error('Failed to fetch testers:', err));
  }, [isAuthenticated, mode, formData.organisationId, ineraOrg?.id]);

  useEffect(() => {
    if (selectedTesterEmails.size === 0 || !formData.organisationId) { setTesterContractMap({}); return; }
    const emailArray = [...selectedTesterEmails];
    Promise.all(emailArray.map(email => fetchContractsByTester(email, formData.organisationId).catch(() => [])))
      .then(results => {
        const map = {};
        results.forEach((contracts, i) => {
          const email = emailArray[i];
          const tester = testers.find(t => t.email === email);
          const name = tester ? tester.username : email;
          contracts.forEach(c => {
            if (!map[c.id]) map[c.id] = [];
            map[c.id].push(name);
          });
        });
        setTesterContractMap(map);
      });
  }, [selectedTesterEmails, testers, formData.organisationId]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setSelectedTesterEmails(new Set());
    setTesterContractMap({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const toggleTester = (email) => {
    setSelectedTesterEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) { next.delete(email); } else { next.add(email); }
      return next;
    });
  };

  const toggleContract = (contractId) => {
    setSelectedContractIds(prev => {
      const next = new Set(prev);
      if (next.has(contractId)) {
        next.delete(contractId);
      } else {
        next.add(contractId);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Inline field-level validation for required text/select fields
    const newFieldErrors = {};
    if (!formData.serviceNowId.trim()) {
      newFieldErrors.serviceNowId = 'ServiceNow ärendenummer krävs.';
    }
    if (!formData.organisationId) {
      newFieldErrors.organisationId = 'Välj en organisation.';
    }
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Kontrollera de markerade fälten och försök igen.');
      return;
    }
    setFieldErrors({});

    if (selectedContractIds.size === 0) {
      setError('Välj minst ett tjänstekontrakt.');
      return;
    }
    if (selectedTesterEmails.size === 0) {
      setError('Välj minst en testare.');
      return;
    }
    flushSync(() => {
      setLoading(true);
      setError(null);
      setSuccess(false);
    });

    try {
      await createServiceOrder(
        formData.serviceNowId,
        formData.organisationId,
        Array.from(selectedContractIds),
        Array.from(selectedTesterEmails),
        formData.tpvIssueKey,
        mode === 'VavTP' ? ineraOrg?.id : null
      );
      setSuccess(true);
      setFormData({ organisationId: '', serviceNowId: '', tpvIssueKey: '' });
      setSelectedContractIds(new Set());
      setSelectedTesterEmails(new Set());
      setTesterContractMap({});
    } catch (err) {
      setError(err.message || 'Något gick fel vid skapande av beställningen. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <main className="ids-content ids-p-6" style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <h1 className="ids-heading-l ids-mb-6">Skapa Beställning</h1>

        {error && (
          <IDSAlert type="error" className="ids-mb-4">
            <p>{error}</p>
          </IDSAlert>
        )}
        {success && (
          <IDSAlert type="success" className="ids-mb-4">
            <p>Beställning skapad!</p>
          </IDSAlert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="ids-mb-6">
            <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>Typ</p>
            <IDSButtonGroup unresponsive>
              <IDSButton type="button" secondary={mode !== 'SjD'} onClick={() => handleModeChange('SjD')}>SjD</IDSButton>
              <IDSButton type="button" secondary={mode !== 'VavTP'} onClick={() => handleModeChange('VavTP')}>VavTP</IDSButton>
            </IDSButtonGroup>
          </div>

          <div className="ids-mb-4">
            <IDSInput
              label="ServiceNow ärendenummer *"
              type="text"
              id="serviceNowId"
              name="serviceNowId"
              value={formData.serviceNowId}
              onChange={handleInputChange}
              invalid={!!fieldErrors.serviceNowId}
              errorMsg={fieldErrors.serviceNowId}
            />
          </div>

          <div className="ids-mb-4">
            <IDSSelect
              label="Organisation *"
              id="organisationId"
              name="organisationId"
              value={formData.organisationId}
              onChange={handleInputChange}
              invalid={!!fieldErrors.organisationId}
              errorMsg={fieldErrors.organisationId}
            >
              <option value="">Välj en organisation</option>
              {organisations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </IDSSelect>
          </div>
          <div className="ids-mb-4">
            <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              Testare{mode === 'VavTP' ? ' (Inera)' : ''} * {selectedTesterEmails.size > 0 && (
                <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>
                  ({selectedTesterEmails.size} valda)
                </span>
              )}
            </p>
            {!formData.organisationId ? (
              <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.875rem' }}>Välj en organisation först.</p>
            ) : testers.length === 0 ? (
              <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.875rem' }}>Inga testare hittades för vald organisation.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {testers.map(tester => (
                  <IDSCheckButton
                    key={`${tester.email}-${selectedTesterEmails.has(tester.email)}`}
                    defaultChecked={selectedTesterEmails.has(tester.email)}
                    onChange={() => toggleTester(tester.email)}
                  >
                    {tester.username} <span style={{ color: 'var(--ids-palette-neutral-60)', fontSize: '0.8rem' }}>{tester.email}</span>
                  </IDSCheckButton>
                ))}
              </div>
            )}
          </div>



          <div className="ids-mb-4">
            <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              Tjänstekontrakt * {selectedContractIds.size > 0 && (
                <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>
                  ({selectedContractIds.size} valda)
                </span>
              )}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {contracts.map(contract => {
                const assignedTo = testerContractMap[contract.id];
                return (
                  <IDSCheckButton
                    key={`${contract.id}-${selectedContractIds.has(contract.id)}`}
                    defaultChecked={selectedContractIds.has(contract.id)}
                    onChange={() => toggleContract(contract.id)}
                  >
                    {contract.contractName}{contract.version ? ` ${contract.version}` : ''}
                    {assignedTo && assignedTo.length > 0 && (
                      <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: 'var(--ids-palette-neutral-60)' }}>
                        (tilldelad: {assignedTo.join(', ')})
                      </span>
                    )}
                  </IDSCheckButton>
                );
              })}
            </div>
          </div>

          {/* Källsystem and Tjänsteproducent are answered by the tester in the
              questionnaire — that is where the AO/TPV pair is created from. */}

          <div className="ids-mb-4">
            <IDSInput
              label="Befintligt TPV (Jira-nyckel)"
              type="text"
              id="tpvIssueKey"
              name="tpvIssueKey"
              value={formData.tpvIssueKey}
              onChange={handleInputChange}
              placeholder="t.ex. MIMAV-123"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <IDSButton size="m" disabled={loading} type="submit">
              {loading ? <IDSSpinner size="s" /> : 'Skapa'}
            </IDSButton>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateUserLayout;
